import {
  ChangeEvent,
  FC,
  createElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { QuestionDropdownModel, RendererFactory } from "survey-core";
import { ReactQuestionFactory, ReactElementFactory } from "survey-react-ui";
import { Dropdown } from "../ui/dropdown";

interface DropdownQuestionProps {
  isDisplayMode: boolean;
  question: QuestionDropdownModel;
}

const DropdownQuestion: FC<DropdownQuestionProps> = ({
  isDisplayMode,
  question,
}) => {
  const {
    value,
    isReadOnly,
    name,
    allowClear,
    isRequired,
    placeholder,
    otherItem,
    noneItem,
    hasOther,
    hasNone,
    isOtherSelected,
    commentPlaceHolder,
    cssClasses,
    commentId,
  } = question;

  const [textAreaInput, setTextAreaInput] = useState(question.comment);

  const isDisabled = useMemo(() => {
    return isReadOnly;
  }, [isReadOnly]);

  const onValueChange = useCallback(
    (newVal: string) => {
      question.value = newVal;
    },
    [question]
  );

  const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaInput(event.target.value);
  };

  const onCommentBlur = () => {
    question.comment = textAreaInput;
  };

  const getItems = useMemo(() => {
    const items = question.dataChoices.map((item) => {
      const itemComponent = item.component ?? question.itemComponent;
      let newElement: any;
      if (itemComponent && itemComponent !== "") {
        newElement = ReactElementFactory.Instance.createElement(itemComponent, {
          item,
          key: item.id,
        });
      }
      return {
        title: newElement ?? item.title,
        value: item.value,
      };
    });
    if (hasNone) items.push(noneItem);
    if (hasOther) items.push(otherItem);
    if (allowClear) items.unshift({ value: undefined, title: "" });
    return items;
  }, [
    question.itemComponent,
    question.dataChoices,
    hasNone,
    noneItem,
    hasOther,
    otherItem,
    allowClear,
  ]);

  return (
    <>
      <Dropdown
        aria-required={question.ariaRequired}
        aria-label={question.ariaLabel}
        aria-invalid={question.ariaInvalid}
        aria-errormessage={question.ariaErrormessage}
        autoComplete={question.autocomplete}
        onClick={question.onClick}
        onKeyUp={question.onKeyUp}
        id={question.id}
        items={getItems}
        required={isRequired}
        disabled={isDisabled}
        value={value}
        name={name}
        dense
        onValueChange={onValueChange}
        placeholder={placeholder}
        defaultValue={question.getDefaultValue()}
      />
      {isOtherSelected && (
        <div className={cssClasses.commentArea}>
          <textarea
            id={commentId}
            disabled={isDisabled}
            onChange={onCommentChange}
            onBlur={onCommentBlur}
            className={cssClasses.comment}
            value={textAreaInput}
            placeholder={commentPlaceHolder}
          />
        </div>
      )}
    </>
  );
};

ReactQuestionFactory.Instance.registerQuestion(
  "sv-dropdown-question",
  function (props: any) {
    return createElement(DropdownQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "dropdown",
  "dropdown-question",
  "sv-dropdown-question"
);
